import { useState, useEffect, useCallback, useMemo } from 'react';
import { backup } from '../services/apiService';
import {
  Database,
  Download,
  Trash2,
  RefreshCw,
  Upload,
  Clock,
  HardDrive,
  AlertTriangle,
  CheckCircle,
  Info,
  FileDown,
  FileUp
} from 'lucide-react';
import { Loader } from '../components/UI/LoadingSpinner';
import { Table } from '@anyemp/ui-kit';
import useResponsiveViewPreference from '../hooks/useResponsiveViewPreference';
import DataViewToggle from '../components/UI/DataViewToggle';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import { RecordCard, RecordCardField, RecordCardFields, RecordCardHeader, ResponsiveCardGrid } from '../components/UI/ResponsiveRecordCards';
import CardActionsMenu from '../components/UI/CardActionsMenu';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getTypeIcon = (type) => {
  switch (type) {
    case 'scheduled': return <Clock className="w-4 h-4 text-blue-500" />;
    case 'manual':    return <Download className="w-4 h-4 text-green-500" />;
    case 'restore':   return <Upload className="w-4 h-4 text-purple-500" />;
    case 'upload':    return <FileUp className="w-4 h-4 text-orange-500" />;
    default:          return <Database className="w-4 h-4 text-gray-500" />;
  }
};

const getTypeText = (type) => {
  switch (type) {
    case 'scheduled': return 'Scheduled';
    case 'manual':    return 'Manual';
    case 'restore':   return 'Restore';
    case 'upload':    return 'Uploaded';
    default:          return 'Unknown';
  }
};

const BackupManagement = () => {
  const [backups, setBackups] = useState([]);
  const backupFileRowKey = useCallback((item) => item.filename, []);
  const backupOpRowKey = useCallback(
    (item) => `${item.startedAt || ''}-${item.filename || ''}-${item.type || ''}`,
    []
  );
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const { mode: viewMode, selectMode: setViewMode } = useResponsiveViewPreference('backup-records');

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [backupsRes, statusRes, historyRes] = await Promise.all([
        backup.listBackups(),
        backup.getStatus(),
        backup.getHistory(20, 0)
      ]);

      setBackups(backupsRes.data);
      setStatus(statusRes.data);
      setHistory(historyRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load backup data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Create new backup
  const handleCreateBackup = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccessMessage(null);

      await backup.createBackup();
      setSuccessMessage('Backup created successfully');

      // Reload data
      await loadData();
    } catch (err) {
      console.error('Error creating backup:', err);
      setError(err.response?.data?.error || 'Failed to create backup');
    } finally {
      setActionLoading(false);
    }
  };

  // Restore from backup
  const handleRestoreBackup = async (filename) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccessMessage(null);

      const result = await backup.restoreBackup(filename, true);

      if (result.data?.shouldReload) {
        setSuccessMessage('Database restored successfully! Reloading page...');
        setShowRestoreConfirm(null);

        // Ждём 2 секунды и перезагружаем страницу
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setSuccessMessage('Database restored successfully');
        setShowRestoreConfirm(null);
        await loadData();
      }
    } catch (err) {
      console.error('Error restoring backup:', err);
      setError(err.response?.data?.error || 'Failed to restore database');
      setShowRestoreConfirm(null);
    } finally {
      setActionLoading(false);
    }
  };

  // Download backup
  const handleDownloadBackup = useCallback(async (filename) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccessMessage(null);

      await backup.downloadBackup(filename);
      setSuccessMessage(`Backup ${filename} downloaded successfully`);
    } catch (err) {
      console.error('Error downloading backup:', err);
      setError(err.response?.data?.error || 'Failed to download backup');
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Delete backup
  const handleDeleteBackup = useCallback(async (filename) => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccessMessage(null);

      await backup.deleteBackup(filename);
      setSuccessMessage('Backup deleted successfully');
      setShowDeleteConfirm(null);

      // Reload data
      await loadData();
    } catch (err) {
      console.error('Error deleting backup:', err);
      setError(err.response?.data?.error || 'Failed to delete backup');
      setShowDeleteConfirm(null);
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Upload external backup
  const handleUploadBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    if (!file.name.endsWith('.sql')) {
      setError('Only .sql files are accepted for backup upload.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);
      setSuccessMessage(null);

      await backup.uploadBackup(file, (percent) => setUploadProgress(percent));

      setSuccessMessage(`Backup "${file.name}" uploaded successfully.`);
      setUploadProgress(null);
      await loadData();
    } catch (err) {
      console.error('Error uploading backup:', err);
      setError(err.response?.data?.error || 'Failed to upload backup file.');
      setUploadProgress(null);
    } finally {
      setIsUploading(false);
    }
  };

  const backupsColumns = useMemo(() => [
    {
      key: 'filename',
      label: 'File',
      render: (item) => (
        <div className="flex items-center">
          <Database className="w-5 h-5 text-gray-400 mr-2" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {item.filename}
          </span>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (item) => (
        <div className="flex items-center">
          {getTypeIcon(item.type)}
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
            {getTypeText(item.type)}
          </span>
        </div>
      ),
    },
    {
      key: 'size',
      label: 'Size',
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{item.size}</span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(item.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (item) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => handleDownloadBackup(item.filename)}
            disabled={actionLoading}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-green-600 transition-colors hover:bg-green-50 hover:text-green-900 dark:text-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-300"
            title="Download"
          >
            <FileDown className="w-5 h-5 inline" />
          </button>
          <button
            onClick={() => setShowRestoreConfirm(item.filename)}
            disabled={actionLoading}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300"
            title="Restore"
          >
            <Upload className="w-5 h-5 inline" />
          </button>
          <button
            onClick={() => setShowDeleteConfirm(item.filename)}
            disabled={actionLoading}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-red-600 transition-colors hover:bg-red-50 hover:text-red-900 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
            title="Delete"
          >
            <Trash2 className="w-5 h-5 inline" />
          </button>
        </div>
      ),
    },
  ], [actionLoading, handleDownloadBackup]);

  const historyColumns = useMemo(() => [
    {
      key: 'type',
      label: 'Operation',
      render: (item) => (
        <div className="flex items-center">
          {getTypeIcon(item.type)}
          <span className="ml-2 text-sm text-gray-900 dark:text-white">
            {getTypeText(item.type)}
          </span>
        </div>
      ),
    },
    {
      key: 'filename',
      label: 'File',
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{item.filename}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          item.status === 'success'
            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
            : item.status === 'failed'
            ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
        }`}>
          {item.status === 'success' ? 'Success' : item.status === 'failed' ? 'Failed' : 'In Progress'}
        </span>
      ),
    },
    {
      key: 'startedAt',
      label: 'Time',
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(item.startedAt)}</span>
      ),
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (item) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{item.duration || '-'}</span>
      ),
    },
  ], []);

  if (loading) {
    return <Loader variant="component" size="md" />;
  }

  return (
    <div>
      <title>Backup | Talent Service</title>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[var(--palette-text-primary)] mb-2">
          Database Backup Management
        </h1>
        <p className="text-[var(--palette-text-secondary)]">
          Create, restore, and manage database backups
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-green-800 dark:text-green-200">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Status Panel */}
      {status && (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="rounded-xl border bg-[var(--palette-background-paper)] border-[var(--palette-divider)] shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--palette-text-secondary)] mb-1">Status</p>
                <p className="text-2xl font-bold text-[var(--palette-text-primary)]">
                  {status.enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                status.enabled ? 'bg-green-100 dark:bg-green-900/20' : 'bg-gray-100 dark:bg-gray-700'
              }`}>
                <Database className={`w-6 h-6 ${
                  status.enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                }`} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-[var(--palette-background-paper)] border-[var(--palette-divider)] shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--palette-text-secondary)] mb-1">Total Backups</p>
                <p className="text-2xl font-bold text-[var(--palette-text-primary)]">
                  {status.currentBackupCount} / {status.maxBackups}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-[var(--palette-background-paper)] border-[var(--palette-divider)] shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--palette-text-secondary)] mb-1">Total Size</p>
                <p className="text-2xl font-bold text-[var(--palette-text-primary)]">
                  {status.totalBackupSize}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-[var(--palette-background-paper)] border-[var(--palette-divider)] shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--palette-text-secondary)] mb-1">Schedule</p>
                <p className="text-sm font-medium text-[var(--palette-text-primary)]">
                  {status.scheduler?.schedule || 'Not configured'}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mb-8 flex flex-wrap gap-4">
        <button
          onClick={handleCreateBackup}
          disabled={actionLoading || status?.isBackupInProgress}
          className="flex min-h-10 items-center rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          <Download className="w-5 h-5 mr-2" />
          Create Backup
        </button>

        <button
          onClick={() => document.getElementById('backup-file-input').click()}
          disabled={actionLoading || isUploading}
          className="flex min-h-10 items-center rounded-lg bg-orange-600 px-4 py-2 text-white transition-colors hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          <FileUp className="w-5 h-5 mr-2" />
          {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Backup'}
        </button>

        <input
          id="backup-file-input"
          type="file"
          accept=".sql"
          className="hidden"
          onChange={handleUploadBackup}
        />

        <button
          onClick={loadData}
          disabled={loading}
          className="flex min-h-10 items-center rounded-lg bg-gray-600 px-4 py-2 text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Upload Progress */}
      {isUploading && uploadProgress !== null && (
        <div className="mb-6 rounded-xl border bg-[var(--palette-background-paper)] border-[var(--palette-divider)] shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[var(--palette-text-primary)]">Uploading backup...</span>
            <span className="text-sm text-[var(--palette-text-secondary)]">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-orange-600 h-2 rounded-full transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Backups List */}
      <div className="rounded-xl border bg-[var(--palette-background-default)] border-[var(--palette-divider)] shadow-sm overflow-hidden mb-8">
        <div className="px-5 py-4 border-b border-[var(--palette-divider)] bg-[var(--palette-background-paper)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold text-[var(--palette-text-primary)] flex items-center gap-2">
              <Database className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              Backups ({backups.length})
            </h3>
            <DataViewToggle value={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {viewMode === 'cards' ? (
          backups.length ? <ResponsiveCardGrid className="p-4">
            {backups.map((item) => (
              <RecordCard key={item.filename}>
                <RecordCardHeader title={item.filename} actions={<CardActionsMenu>{backupsColumns.find((column) => column.key === 'actions')?.render(item)}</CardActionsMenu>} />
                <RecordCardFields>
                  <RecordCardField label="Size">{item.size}</RecordCardField>
                  <RecordCardField label="Created">{formatDate(item.createdAt)}</RecordCardField>
                </RecordCardFields>
              </RecordCard>
            ))}
          </ResponsiveCardGrid> : <div className="p-8 text-center text-gray-500 dark:text-gray-400">No backups available</div>
        ) : (
          <Table columns={backupsColumns} data={backups} rowKey={backupFileRowKey} variant="ghost" emptyState={<div className="p-8 text-center text-gray-500 dark:text-gray-400"><Database className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No backups available</p></div>} />
        )}
      </div>

      {/* Operation History */}
      <div className="rounded-xl border bg-[var(--palette-background-default)] border-[var(--palette-divider)] shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--palette-divider)] bg-[var(--palette-background-paper)]">
          <h3 className="font-semibold text-[var(--palette-text-primary)] flex items-center gap-2">
            <Info className="h-4 w-4 text-[var(--palette-text-secondary)]" />
            Operation History
          </h3>
        </div>

        <Table columns={historyColumns} data={history} rowKey={backupOpRowKey} variant="ghost" emptyState={<div className="p-8 text-center text-gray-500 dark:text-gray-400"><Info className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No operation history</p></div>} />
      </div>

      {/* Restore Confirmation Modal */}
      <ConfirmDialog
        isOpen={Boolean(showRestoreConfirm)}
        onClose={() => setShowRestoreConfirm(null)}
        onConfirm={() => handleRestoreBackup(showRestoreConfirm)}
        title="Confirm Restore"
        message={<>Are you sure you want to restore the database from backup <strong>{showRestoreConfirm}</strong>? This action will overwrite the current database.</>}
        confirmText="Restore"
        confirmStyle="warning"
        icon={AlertTriangle}
        iconColor="text-yellow-600"
        isLoading={actionLoading}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={Boolean(showDeleteConfirm)}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() => handleDeleteBackup(showDeleteConfirm)}
        title="Confirm Delete"
        message={<>Are you sure you want to delete backup <strong>{showDeleteConfirm}</strong>? This action cannot be undone.</>}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={actionLoading}
      />
    </div>
  );
};

export default BackupManagement;
